import { useEffect } from "react";
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, TRANSLATIONS_LIVE, parseLocaleFromPath, buildLocalizedPath } from "@/lib/i18n/locales";

interface PageMetaOptions {
  title: string;
  description?: string;
  /** Path only, e.g. "/tool/merge-pdf". Used to build the canonical URL. */
  path?: string;
  /** Set to true only for pages actually registered under locale-prefixed
   * routes (Home, tool pages, Contact) -- everything else defaults to
   * false so hreflang tags never point at URLs that don't exist yet
   * (e.g. /es/about, which isn't a real route tonight). Actual emission
   * is further gated on TRANSLATIONS_LIVE (see locales.ts) so this alone
   * won't advertise untranslated placeholder pages as language alternates. */
  localized?: boolean;
  /** Absolute or root-relative URL to the image for og:image/twitter:image
   * (e.g. a blog post's own cover image). Falls back to the sitewide
   * default banner when omitted. */
  image?: string;
  /** When true, emit robots noindex,nofollow — use for soft-404 / unknown tool pages
   * so Google does not treat 200 "not found" UI as indexable content. */
  noindex?: boolean;
  /** Content that exists in English only (blog posts) declares just itself plus x-default, never the translated tool-page set.
   * Takes precedence over `localized`. See setSelfHreflangTags. */
  englishOnly?: boolean;
}

const DEFAULT_OG_IMAGE = "https://www.freedailypro.com/og-default.png";

function toAbsoluteImageUrl(image: string | undefined): string {
  if (!image) return DEFAULT_OG_IMAGE;
  return image.startsWith("http") ? image : `https://www.freedailypro.com${image}`;
}

const SITE_NAME = "FreeDailyPro";
const BASE_URL = "https://www.freedailypro.com";
const DEFAULT_TITLE = "FreeDailyPro - Online Tools for PDF, Images & More";
const DEFAULT_DESCRIPTION =
  "300+ online tools for PDF, images, video, and text, plus workflows, API and MCP. Most tools need no signup at all — just open and use.";

function setMetaTag(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Remove every hreflang alternate in the document, including the set the server (or the prerender step) put in the HTML,
 * which carries no data-hreflang marker. Leaving it behind doubled the set after hydration (B-0052). */
export function removeHreflangTags(root: ParentNode = document) {
  root.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
}

function setHreflangTags(rest: string) {
  removeHreflangTags();

  for (const locale of SUPPORTED_LOCALES) {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", locale);
    el.setAttribute("href", `${BASE_URL}${buildLocalizedPath(locale, rest)}`);
    el.setAttribute("data-hreflang", "true");
    document.head.appendChild(el);
  }
  const defaultEl = document.createElement("link");
  defaultEl.setAttribute("rel", "alternate");
  defaultEl.setAttribute("hreflang", "x-default");
  defaultEl.setAttribute("href", `${BASE_URL}${buildLocalizedPath(DEFAULT_LOCALE, rest)}`);
  defaultEl.setAttribute("data-hreflang", "true");
  document.head.appendChild(defaultEl);
}

/** English-only pages: one self-referencing "en" alternate plus x-default, both pointing at the English URL. No half-state. */
export function setSelfHreflangTags(path: string) {
  removeHreflangTags();
  for (const hreflang of [DEFAULT_LOCALE, "x-default"]) {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", `${BASE_URL}${path}`);
    el.setAttribute("data-hreflang", "true");
    document.head.appendChild(el);
  }
}

function clearHreflangTags() {
  removeHreflangTags();
}

/**
 * Sets document title + meta description/OG/canonical for the current page.
 * Resets to site defaults on unmount so navigating away (e.g. to a route
 * that doesn't call this hook) doesn't leave a stale title behind.
 *
 * og:image/twitter:image use the given `image` (e.g. a blog post's own
 * cover) or fall back to the sitewide default banner.
 *
 * Emits hreflang alternate tags for pages registered under locale-prefixed
 * routes (Home, tool pages, Contact) -- pass localized={false} to opt out
 * for pages that aren't locale-routed -- but only once TRANSLATIONS_LIVE
 * is true, since es/pt/fr are still placeholder English today and
 * hreflang would misrepresent them as real translated alternates.
 */
export function usePageMeta({ title, description, path, localized = false, image, noindex = false, englishOnly = false }: PageMetaOptions) {
  useEffect(() => {
    // Keep titles within ~65 chars so search results do not truncate them:
    // drop the brand suffix when it would push the title past that.
    const brandSuffix = ` | ${SITE_NAME}`;
    const withBrand = title ? (title.endsWith(brandSuffix) ? title : `${title}${brandSuffix}`) : DEFAULT_TITLE;
    const fullTitle = title && withBrand.length > 65 ? title : withBrand;
    const desc = description || DEFAULT_DESCRIPTION;

    // While translations aren't live, a locale-prefixed page (e.g.
    // /es/tool/merge-pdf) renders byte-identical English content to its
    // real English original -- so it must canonicalize TO that original,
    // not to itself. Self-referencing was the actual bug here: Google
    // was seeing 4 pages (en/es/fr/pt) with identical content, each
    // declaring itself canonical, and understandably concluding those
    // self-declarations couldn't all be trusted -- which is precisely
    // the "duplicate, Google chose a different canonical than the site
    // declared" signal Search Console was flagging on ~102 pages. Once
    // TRANSLATIONS_LIVE flips true, this reverts to self-referencing
    // (each locale becomes real, distinct content worth its own
    // canonical) automatically -- nothing else here needs to change.
    const canonicalPath = localized && path && !TRANSLATIONS_LIVE ? parseLocaleFromPath(path).rest : path;
    const canonicalUrl = canonicalPath ? `${BASE_URL}${canonicalPath}` : BASE_URL;
    const imageUrl = toAbsoluteImageUrl(image);

    document.title = fullTitle;
    // <html lang> follows the locale of the URL (the server sets it in the raw HTML; keep it right on client navigation too).
    if (path) document.documentElement.lang = parseLocaleFromPath(path).locale;
    setMetaTag("name", "description", desc);
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", desc);
    setMetaTag("property", "og:url", canonicalUrl);
    setMetaTag("property", "og:image", imageUrl);
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", desc);
    setMetaTag("name", "twitter:image", imageUrl);
    // A noindex page (404, running pipeline) must not claim the homepage as its canonical.
    if (noindex) document.querySelector('link[rel="canonical"]')?.remove();
    else setCanonical(canonicalUrl);

    // Soft-404 / unknown pages: tell crawlers not to index even if status is 200 in SPA.
    if (noindex) {
      setMetaTag("name", "robots", "noindex, nofollow");
    } else {
      const robots = document.querySelector('meta[name="robots"]');
      if (robots) robots.remove();
    }

    // Gated on TRANSLATIONS_LIVE: es/pt/fr are still placeholder English
    // right now, so hreflang tags would tell search engines these are
    // genuine translated alternates when they aren't. Re-enable by
    // flipping that one flag once real translations land -- nothing else
    // here needs to change.
    if (englishOnly && path && !noindex) {
      setSelfHreflangTags(parseLocaleFromPath(path).rest);
    } else if (localized && path && TRANSLATIONS_LIVE) {
      const { rest } = parseLocaleFromPath(path);
      setHreflangTags(rest);
    } else {
      clearHreflangTags();
    }

    return () => {
      document.title = DEFAULT_TITLE;
      setMetaTag("name", "description", DEFAULT_DESCRIPTION);
      setMetaTag("property", "og:title", DEFAULT_TITLE);
      setMetaTag("property", "og:description", DEFAULT_DESCRIPTION);
      setMetaTag("property", "og:url", BASE_URL);
      setMetaTag("property", "og:image", DEFAULT_OG_IMAGE);
      setMetaTag("name", "twitter:title", DEFAULT_TITLE);
      setMetaTag("name", "twitter:description", DEFAULT_DESCRIPTION);
      setMetaTag("name", "twitter:image", DEFAULT_OG_IMAGE);
      setCanonical(BASE_URL);
      clearHreflangTags();
      const robots = document.querySelector('meta[name="robots"]');
      if (robots) robots.remove();
    };
  }, [title, description, path, localized, image, noindex, englishOnly]);
}
